import type { AiMarkerKind } from "@repo/ai-disclaimer";
import { AI_MARKER_KINDS } from "./aiImagesInventory";

export type AiIconVariant = "light" | "dark";

/** One icon URL per marker kind, in a light and a dark version. */
export type AiIconSet = Record<AiMarkerKind, Record<AiIconVariant, string>>;

/**
 * A partially-configured icon set, which is what a CMS hands over in practice: an editor
 * may have bound the `generated` pair and not yet the others.
 */
export type PartialAiIconSet = {
  [K in AiMarkerKind]?: Partial<Record<AiIconVariant, string>>;
};

/**
 * Marker for the placeholder artwork, greppable across every consuming repo.
 *
 * Mirrors the `PENDING_LEGAL_` convention the disclaimer copy uses: these six icons are
 * stand-ins until real assets are bound through the asset-management page, and the
 * marker is what makes "are we still shipping placeholders?" a one-command question.
 */
export const PENDING_ASSET_MARKER = "PENDING_ASSET";

const PLACEHOLDER_LABEL: Record<AiMarkerKind, string> = {
  generated: "AI GEN",
  modified: "AI EDIT",
  unspecified: "AI",
};

const VARIANT_COLORS: Record<AiIconVariant, { fill: string; text: string }> = {
  // `light` is the badge to use ON a light image, so the pill itself is dark.
  light: { fill: "#242424", text: "#ffffff" },
  dark: { fill: "#ffffff", text: "#242424" },
};

/**
 * Builds a placeholder badge as an inline SVG data URI.
 *
 * A data URI rather than a bundled `.svg` file so the badge has exactly one render path:
 * the real icons arrive as remote CMS URLs, and an `<img src>` consumes both forms
 * identically. A file import would need per-app static-asset handling and would still
 * have to be swapped for a URL later.
 *
 * `PENDING_ASSET` is embedded in the SVG itself, not just the surrounding code, so it
 * survives into the rendered DOM and can be found from a browser or a page scrape.
 */
function placeholderIcon(kind: AiMarkerKind, variant: AiIconVariant): string {
  const { fill, text } = VARIANT_COLORS[variant];
  const label = PLACEHOLDER_LABEL[kind];
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="24" viewBox="0 0 64 24" role="img" data-${PENDING_ASSET_MARKER}="${kind}-${variant}">`,
    `<rect width="64" height="24" rx="12" fill="${fill}" fill-opacity="0.82"/>`,
    `<text x="32" y="16" font-family="system-ui,sans-serif" font-size="10" font-weight="600" text-anchor="middle" fill="${text}">${label}</text>`,
    `</svg>`,
  ].join("");

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** The bundled stand-in set, used wherever no icon has been bound yet. */
export const PLACEHOLDER_AI_ICONS: AiIconSet = {
  generated: {
    light: placeholderIcon("generated", "light"),
    dark: placeholderIcon("generated", "dark"),
  },
  modified: {
    light: placeholderIcon("modified", "light"),
    dark: placeholderIcon("modified", "dark"),
  },
  unspecified: {
    light: placeholderIcon("unspecified", "light"),
    dark: placeholderIcon("unspecified", "dark"),
  },
};

/**
 * Picks the icon URL for a marker kind and variant, falling back per-entry.
 *
 * The fallback is per-entry rather than all-or-nothing on purpose: a half-configured set
 * is the normal intermediate state while an editor binds six assets one at a time, and
 * falling back to placeholders only for the gaps keeps every image labeled throughout.
 */
export function resolveAiIcon(
  kind: AiMarkerKind,
  variant: AiIconVariant,
  configured?: PartialAiIconSet | null,
): string {
  return configured?.[kind]?.[variant] || PLACEHOLDER_AI_ICONS[kind][variant];
}

/** True when the resolved icon is still bundled artwork rather than a bound asset. */
export function isPlaceholderAiIcon(url: string): boolean {
  return url.includes(PENDING_ASSET_MARKER);
}

/** Every variant, derived once so a loop cannot fall out of step with the type. */
export const AI_ICON_VARIANTS: readonly AiIconVariant[] = ["light", "dark"];

/**
 * One bound icon url, or nothing bound yet.
 *
 * A url rather than an asset reference: the artwork does not have to live in this
 * Storyblok space — a shared brand CDN, another space, or a data URI are all legitimate.
 * `id` is the Storyblok asset id when the url happens to name one, so the picker can
 * preselect it, and `0` otherwise.
 */
export type AiIconBinding = { id: number; filename: string } | null;

export type AiIconBindings = Record<
  AiMarkerKind,
  Record<AiIconVariant, AiIconBinding>
>;

/**
 * The `aiImagesConfig` field name holding one binding, e.g. `icon_generated_light`.
 *
 * Derived rather than listed so the six fields, the CMS schema and the UI cannot drift.
 */
export function aiIconFieldName(
  kind: AiMarkerKind,
  variant: AiIconVariant,
): string {
  return `icon_${kind}_${variant}`;
}

/**
 * Reads one stored icon field into a binding.
 *
 * Two shapes are accepted on purpose. A plain string is what the binding UI stores, and
 * is what lets the icon be hosted anywhere. An object is Storyblok's own asset-field
 * shape, so a field bound through the CMS asset picker rather than this page still reads
 * back correctly.
 *
 * Storyblok writes `{}` — not `null` — into an asset field an editor has cleared, and a
 * field never touched is absent entirely. Both mean "nothing bound", so both must collapse
 * to `null` here; treating `{}` as bound would hand `undefined` to an `<img src>` and
 * silently break the badge instead of falling back to the placeholder.
 */
export function parseIconField(value: unknown): AiIconBinding {
  if (typeof value === "string") {
    return value.trim() ? { id: 0, filename: value.trim() } : null;
  }

  if (!value || typeof value !== "object") return null;

  const { id, filename } = value as { id?: unknown; filename?: unknown };

  if (typeof filename !== "string" || !filename.trim()) return null;

  return { id: typeof id === "number" ? id : 0, filename };
}

/** All six bindings from an `aiImagesConfig` story's content. */
export function readIconBindings(
  content: Record<string, unknown> | null | undefined,
): AiIconBindings {
  const bindings = {} as AiIconBindings;

  for (const kind of AI_MARKER_KINDS) {
    bindings[kind] = {} as Record<AiIconVariant, AiIconBinding>;

    for (const variant of AI_ICON_VARIANTS) {
      bindings[kind][variant] = parseIconField(
        content?.[aiIconFieldName(kind, variant)],
      );
    }
  }

  return bindings;
}

/**
 * Bindings in the shape the render path consumes.
 *
 * Unbound entries are omitted rather than set to an empty string, because `resolveAiIcon`
 * falls back per entry: a half-bound set must still render a placeholder for its gaps.
 */
export function bindingsToIconSet(bindings: AiIconBindings): PartialAiIconSet {
  const set: PartialAiIconSet = {};

  for (const kind of AI_MARKER_KINDS) {
    for (const variant of AI_ICON_VARIANTS) {
      const binding = bindings[kind]?.[variant];

      if (!binding) continue;

      set[kind] = { ...set[kind], [variant]: binding.filename };
    }
  }

  return set;
}
